/**
 * Decodes a base64url string (RFC 4648 §5) into an `ArrayBuffer`. Used when reading the credential
 * fields produced by the WebAuthn / passkey API.
 * @param base64url - The base64url-encoded string (no padding, `-`/`_` instead of `+`/`/`).
 * @returns The decoded bytes as an `ArrayBuffer`.
 * @category Utils
 */
export function base64URLToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(base64url.length + (4 - base64url.length % 4) % 4, '=')

  const binary = atob(base64)
  return Uint8Array.from(binary, (c) => c.charCodeAt(0)).buffer
}

/**
 * Encodes an `ArrayBuffer` as a base64url string (RFC 4648 §5) - `+`/`/` swapped for `-`/`_` and
 * trailing padding stripped. Used when sending credential fields to the WebAuthn / passkey API.
 * @param buffer - The bytes to encode.
 * @returns The base64url-encoded string.
 * @category Utils
 */
export function bufferToBase64URL(buffer: ArrayBuffer): string {
  const binary = String.fromCharCode(...new Uint8Array(buffer))
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}
